import DeleteIcon from '@/assets/icons/trash-02.svg?react';
import EditIcon from '@/assets/icons/edit-05.svg?react';
import DuplicateIcon from '@/assets/icons/copy-03.svg?react';
import ViewIcon from '@/assets/icons/eye.svg?react';
import StyledButton from './StyledButton';
import StyledTooltipContainer from './StyledTooltipContainer';
import {
  AlertCircleIcon,
  ArchiveRestoreIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DownloadIcon,
  ListIcon,
} from 'lucide-react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import ShareIcon from '@/assets/icons/share-06.svg?react';
import { useTranslation } from '@/i18n';

interface StyledTableHeaderProps {
  onDelete?: () => void;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onView?: () => void;
  onExpand?: () => void;
  onReduce?: () => void;
  onRefuse?: () => void;
  onAccept?: () => void;
  onAdd?: () => void;
  onCancel?: () => void;
  onShare?: () => void;
  onList?: () => void;
  onRestore?: () => void;
  onDownload?: () => void;
  alertMessage?: string;
  shareEnablePredicate?: () => boolean;
  duplicateEnablePredicate?: () => boolean;
  listEnablePredicate?: () => boolean;
  acceptRefuseEnablePredicate?: () => boolean;
  editEnablePredicate?: () => boolean;
  alertEnablePredicate?: () => boolean;
  onAlertClick?: () => void;
}

const StyledTableActionsRow = ({
  onDelete,
  onEdit,
  onDuplicate,
  onView,
  onExpand,
  onReduce,
  onAccept,
  onRefuse,
  onAdd,
  onCancel,
  onShare,
  onList,
  onRestore,
  onDownload,
  alertMessage,
  onAlertClick,
  shareEnablePredicate = () => true,
  duplicateEnablePredicate = () => true,
  listEnablePredicate = () => true,
  acceptRefuseEnablePredicate = () => true,
  editEnablePredicate = () => true,
  alertEnablePredicate = () => true,
}: StyledTableHeaderProps) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-row-reverse">
      {onDownload && (
        <StyledTooltipContainer
          tooltipMessage={t('common.tableActions.download')}
          asChild
        >
          <StyledButton
            icon={
              <DownloadIcon className="w-6 h-6 stroke-gray-500 hover:stroke-brand-600" />
            }
            variant="link"
            className="mx-1 p-2 hover:bg-brand-50"
            title=""
            onClick={() => onDownload()}
          />
        </StyledTooltipContainer>
      )}
      {alertMessage && alertEnablePredicate() && (
        <StyledTooltipContainer tooltipMessage={alertMessage} asChild>
          <StyledButton
            icon={
              <AlertCircleIcon className="w-6 h-6 stroke-error-500 hover:stroke-error-700" />
            }
            variant="link"
            className="mx-1 p-2 hover:bg-brand-50"
            title=""
            onClick={onAlertClick ?? undefined}
          />
        </StyledTooltipContainer>
      )}
      {onRestore && (
        <StyledTooltipContainer
          tooltipMessage={t('common.tableActions.restore')}
          asChild
        >
          <StyledButton
            icon={
              <ArchiveRestoreIcon className="w-6 h-6 stroke-gray-500 hover:stroke-brand-600" />
            }
            variant="link"
            className="mx-1 p-2 hover:bg-brand-50"
            title=""
            onClick={() => onRestore()}
          />
        </StyledTooltipContainer>
      )}
      {onAdd && (
        <StyledTooltipContainer
          tooltipMessage={t('common.tableActions.add')}
          asChild
        >
          <StyledButton
            icon={
              <PlusIcon className="w-6 h-6 fill-brand-600 hover:fill-brand-700" />
            }
            variant="link"
            className="mx-1 p-2 hover:bg-brand-50"
            title=""
            onClick={() => onAdd()}
          />
        </StyledTooltipContainer>
      )}
      {onAccept && acceptRefuseEnablePredicate() && (
        <StyledButton
          title={t('common.tableActions.accept')}
          onClick={() => onAccept()}
          className="bg-success-600 px-3 py-2 border-success-700 hover:bg-success-700"
        />
      )}
      {onRefuse && acceptRefuseEnablePredicate() && (
        <StyledButton
          variant="link"
          title={t('common.tableActions.refuse')}
          onClick={() => onRefuse()}
          className="px-3 py-2 text-error-600"
        />
      )}
      {onCancel && (
        <StyledButton
          variant="link"
          title={t('common.tableActions.cancel')}
          onClick={() => onCancel()}
          className="px-3 py-2 text-error-600"
        />
      )}
      {onDelete && (
        <StyledTooltipContainer
          tooltipMessage={t('common.tableActions.delete')}
          asChild
        >
          <StyledButton
            icon={
              <DeleteIcon className="w-6 h-6 stroke-gray-500 hover:stroke-brand-600" />
            }
            variant="link"
            className="mx-1 p-2 hover:bg-brand-50"
            title=""
            onClick={() => onDelete()}
          />
        </StyledTooltipContainer>
      )}
      {onList && listEnablePredicate() && (
        <StyledTooltipContainer
          tooltipMessage={t('common.tableActions.list')}
          asChild
        >
          <StyledButton
            icon={
              <ListIcon className="w-6 h-6 stroke-gray-500 hover:stroke-brand-600" />
            }
            variant="link"
            className="mx-1 p-2 hover:bg-brand-50"
            title=""
            onClick={() => onList()}
          />
        </StyledTooltipContainer>
      )}
      {onEdit && editEnablePredicate() && (
        <StyledTooltipContainer
          tooltipMessage={t('common.tableActions.edit')}
          asChild
        >
          <StyledButton
            icon={
              <EditIcon className="w-6 h-6 stroke-gray-500 hover:stroke-brand-600" />
            }
            variant="link"
            className="mx-1 p-2 hover:bg-brand-50"
            title=""
            onClick={() => onEdit()}
          />
        </StyledTooltipContainer>
      )}
      {onShare && shareEnablePredicate() && (
        <StyledTooltipContainer
          tooltipMessage={t('common.tableActions.share')}
          asChild
        >
          <StyledButton
            icon={
              <ShareIcon className="w-6 h-6 stroke-gray-500 hover:stroke-brand-600" />
            }
            variant="link"
            className="mx-1 p-2 hover:bg-brand-50"
            title=""
            onClick={() => onShare()}
          />
        </StyledTooltipContainer>
      )}
      {onDuplicate && duplicateEnablePredicate() && (
        <StyledTooltipContainer
          tooltipMessage={t('common.tableActions.duplicate')}
          asChild
        >
          <StyledButton
            icon={
              <DuplicateIcon className="w-6 h-6 stroke-gray-500 hover:stroke-brand-600" />
            }
            variant="link"
            className="mx-1 p-2 hover:bg-brand-50"
            title=""
            onClick={() => onDuplicate()}
          />
        </StyledTooltipContainer>
      )}
      {onView && (
        <StyledTooltipContainer
          tooltipMessage={t('common.tableActions.view')}
          asChild
        >
          <StyledButton
            icon={
              <ViewIcon className="w-6 h-6 stroke-gray-500 hover:stroke-brand-600" />
            }
            variant="link"
            className="mx-1 p-2 hover:bg-brand-50"
            title=""
            onClick={() => onView()}
          />
        </StyledTooltipContainer>
      )}
      {onExpand && (
        <StyledTooltipContainer
          tooltipMessage={t('common.tableActions.expand')}
          asChild
        >
          <StyledButton
            icon={
              <ChevronDownIcon className="size-8 stroke-gray-500 hover:stroke-brand-600" />
            }
            variant="link"
            className="mx-1 p-2 hover:bg-brand-50"
            title=""
            onClick={() => onExpand()}
          />
        </StyledTooltipContainer>
      )}
      {onReduce && (
        <StyledTooltipContainer
          tooltipMessage={t('common.tableActions.reduce')}
          asChild
        >
          <StyledButton
            icon={
              <ChevronUpIcon className="size-8 stroke-gray-500 hover:stroke-brand-600" />
            }
            variant="link"
            className="mx-1 p-2 hover:bg-brand-50"
            title=""
            onClick={() => onReduce()}
          />
        </StyledTooltipContainer>
      )}
    </div>
  );
};

export default StyledTableActionsRow;
